import { ChangeDetectorRef, Component, HostListener, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';

import { CoreMenuService } from '@core/components/core-menu/core-menu.service';
import { CoreConfigService } from '@core/services/config.service';
import { CoreMenuItem } from '@core/types';
import { isImageIcon, isLucideIcon, lucideIconName } from '@core/utils';

import { Report } from '@bpm/models';
import { ReportService } from '@bpm/services';
import { AuthenticationService } from '@common/services';
import { Content, FileContent, Headquarter, HeadquarterChild, Repository } from '@repositories/models';
import { HeadquartersService, RepositoryBaseService, RepositoryFolderService } from '@repositories/services';

import { COMMUNITY_CATEGORY_NAME } from 'app/main/apps/dashboards/dashboards.constants';
import { MenuActionService } from 'app/helpers/menu-action.service';

/** Contenido que muestra el panel */
export type EdgePanelMode = 'menu' | 'dashboards' | 'belivpedia' | 'headquarters';

/**
 * Nodo del árbol del panel. Sirve tanto para las opciones del menú como para
 * los dashboards: los dos se dibujan con la misma plantilla recursiva.
 */
export interface EdgeNode {
  key: string;
  label: string;
  /** Clase de ícono o URL de imagen (las opciones del menú traen ícono) */
  icon?: string;
  /** El ícono es un nombre de lucide y no una clase del iconfont de feather */
  lucide?: boolean;
  /** Ruta interna, o sitio externo si `external` */
  url?: string;
  external?: boolean;
  newTab?: boolean;
  /** Opción del menú de tipo acción: en vez de navegar, hace la petición */
  action?: CoreMenuItem;
  /** State de navegación (los dashboards le pasan el reporte al visor) */
  state?: any;
  children?: EdgeNode[];

  /** Arranca plegado (las bibliotecas, que traen sus hijos recién al abrirse) */
  defaultCollapsed?: boolean;

  // ── Carga perezosa (belivpedia) ──────────────────────────────
  /** Puede tener hijos, pero todavía no se pidieron */
  lazy?: boolean;
  loading?: boolean;
  loaded?: boolean;
  /** Se cargó y no tenía nada adentro */
  empty?: boolean;
  /** Biblioteca a la que pertenece el nodo */
  repo?: string;
  /** Ids de las carpetas por las que se llega hasta acá */
  ancestors?: string[];
}

/**
 * Panel lateral tipo "edge panel" de Samsung: una manija fija en el borde
 * izquierdo que despliega un árbol de accesos.
 *
 * Muestra las opciones del menú del usuario y cambia de contenido según la
 * sección: dentro de dashboards, el árbol de dashboards por categoría (sin los
 * comunitarios); dentro de belivpedia, las bibliotecas con sus archivos; dentro
 * de headquarters, los headquarters con sus áreas.
 */
@Component({
  selector: 'app-edge-panel',
  templateUrl: './edge-panel.component.html',
  styleUrls: ['./edge-panel.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class EdgePanelComponent implements OnInit, OnDestroy {

  /**
   * Con `false` el panel se esconde mientras haya algo en pantalla completa.
   * Lo usa la instancia global del layout: en pantalla completa solo se ve el
   * elemento que la pidió, así que ahí toma el relevo la instancia que vive
   * dentro de esa pantalla.
   */
  @Input() hideOnFullscreen: boolean = false;

  public open: boolean = false;
  public loading: boolean = false;
  public searchTerm: string = '';

  /** Qué se está mostrando, según la ruta activa */
  public mode: EdgePanelMode = 'menu';

  /** Árbol ya filtrado por la búsqueda, listo para dibujar */
  public nodes: EdgeNode[] = [];

  /**
   * Nodos a los que el usuario les invirtió el estado. La mayoría arranca
   * abierto y las bibliotecas plegadas (ver `defaultCollapsed`), así que se
   * guarda el cambio y no el estado.
   */
  private _toggled = new Set<string>();

  /** Hay algo en pantalla completa (ver hideOnFullscreen) */
  public docFullscreen: boolean = false;

  /**
   * La pantalla admite el chrome de la app y hay sesión iniciada. Deja fuera al
   * login y compañía (login, error, sesión expirada, no autorizado,
   * mantenimiento), que se configuran con el navbar oculto.
   */
  public enabled: boolean = false;

  /** Expone el helper al template: el ícono puede ser clase o imagen */
  readonly isImageIcon = isImageIcon;

  // Árboles completos (sin filtrar) de cada modo
  private _menuNodes: EdgeNode[] = [];
  private _dashboardNodes: EdgeNode[] = [];
  private _belivpediaNodes: EdgeNode[] = [];
  private _headquartersNodes: EdgeNode[] = [];

  /** Cada sección se pide la primera vez que se entra */
  private _dashboardsLoaded: boolean = false;
  private _belivpediaLoaded: boolean = false;
  private _headquartersLoaded: boolean = false;

  private _currentUrl: string = '';
  private _lang: string = 'es';

  private readonly _dashboardType = 'Dashboard';
  private readonly _communityCategory = COMMUNITY_CATEGORY_NAME;

  /** Prefijo de las rutas que muestran el árbol de dashboards */
  private readonly _dashboardsRoute = '/dashboards';

  /** Prefijo de las rutas de belivpedia (bibliotecas y su contenido) */
  private readonly _belivpediaRoute = '/apps/repositories';

  /** Prefijo de las rutas que muestran el árbol de headquarters */
  private readonly _headquartersRoute = '/apps/headquarters';

  /** contentType con el que el backend marca las carpetas */
  private readonly _folderType = '#folder';

  /** Cuántos archivos se piden por carpeta */
  private readonly _pageSize = 200;

  private _unsubscribeAll: Subject<any>;

  constructor(
    private _coreMenuService: CoreMenuService,
    private _coreConfigService: CoreConfigService,
    private _authenticationService: AuthenticationService,
    private _reportService: ReportService,
    private _repositoryBaseService: RepositoryBaseService,
    private _repositoryFolderService: RepositoryFolderService,
    private _headquartersService: HeadquartersService,
    private _translateService: TranslateService,
    private _router: Router,
    private _cdr: ChangeDetectorRef,
    private _menuActionService: MenuActionService
  ) {
    this._unsubscribeAll = new Subject();
  }

  ngOnInit(): void {
    this._lang = this._translateService.currentLang || 'es';
    this._currentUrl = this._router.url;
    this._resolveMode();
    this._buildMenuNodes();

    // Mismo criterio que el dock de Bryant: hace falta sesión y una pantalla
    // que muestre el chrome de la app
    combineLatest([
      this._authenticationService.onUserValueChanged,
      // El getter está tipado como `any | Observable<any>`, hay que concretarlo
      this._coreConfigService.config as Observable<any>
    ])
      .pipe(
        map(([user, config]) => !!user && !config?.layout?.navbar?.hidden),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe(enabled => {
        this.enabled = enabled;
        if (!enabled) this.close();
        this._cdr.detectChanges();
      });

    // El menú se registra después del login y se vuelve a registrar al cambiar
    // de empresa: hay que rearmar el árbol cada vez
    this._coreMenuService.onMenuChanged
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        this._buildMenuNodes();
        this._cdr.detectChanges();
      });

    // Al navegar cambia el modo (dentro de dashboards, el árbol de dashboards)
    // y el ítem marcado como activo
    this._router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntil(this._unsubscribeAll))
      .subscribe((e: NavigationEnd) => {
        this._currentUrl = e.urlAfterRedirects || e.url;
        this._resolveMode();
        this._cdr.detectChanges();
      });

    // Los nombres de dashboards y categorías vienen de la base, no del
    // diccionario: hay que rearmar el árbol al cambiar de idioma
    this._translateService.onLangChange
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(event => {
        this._lang = event.lang;
        this._buildMenuNodes();
        this._buildDashboardNodes();
        this._rebuild();
        this._cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // ──────────────────────────────────────────────────────────────
  // Apertura / cierre
  // ──────────────────────────────────────────────────────────────
  toggle(): void {
    this.open = !this.open;
  }

  close(): void {
    this.open = false;
  }

  /** Con Esc se cierra, como cualquier panel flotante */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) {
      this.close();
      this._cdr.detectChanges();
    }
  }

  /**
   * En pantalla completa el navegador solo dibuja el elemento que la pidió: la
   * instancia global se esconde y deja lugar a la que vive adentro.
   */
  @HostListener('document:fullscreenchange')
  @HostListener('document:webkitfullscreenchange')
  onFullscreenChange(): void {
    this.docFullscreen = !!(document.fullscreenElement || (document as any).webkitFullscreenElement);
    if (this.docFullscreen && this.hideOnFullscreen) this.close();
    this._cdr.detectChanges();
  }

  /**
   * El panel se dibuja si la pantalla lo admite y no está tapado por una
   * pantalla completa ajena
   */
  get visible(): boolean {
    return this.enabled && !(this.hideOnFullscreen && this.docFullscreen);
  }

  /** Título del panel según lo que se esté mostrando */
  get titleKey(): string {
    if (this.mode === 'dashboards') return 'PAGES.HOME.DASHBOARDS';
    if (this.mode === 'belivpedia') return 'BELIVPEDIA.SECTIONS.LIBRARIES';
    if (this.mode === 'headquarters') return 'HEADQUARTERS.MAP.TITLE';
    return 'COMMON.LABEL.MENU';
  }

  /** Ícono del encabezado, según lo que se esté mostrando */
  get titleIcon(): string {
    if (this.mode === 'dashboards') return 'icon-pie-chart';
    if (this.mode === 'belivpedia') return 'icon-book';
    if (this.mode === 'headquarters') return 'icon-home';
    return 'icon-grid';
  }

  // ──────────────────────────────────────────────────────────────
  // Modo según la ruta
  // ──────────────────────────────────────────────────────────────
  private _resolveMode(): void {
    const mode = this._modeForUrl(this._currentUrl);
    const changed = mode !== this.mode;
    this.mode = mode;

    // Al cambiar de modo se limpia la búsqueda: es de lo que se estaba viendo
    if (changed) this.searchTerm = '';

    // El contenido de cada sección se pide recién al entrar, para no cargarlo
    // en todas las pantallas
    if (mode === 'dashboards' && !this._dashboardsLoaded) {
      this._loadDashboards();
      return;
    }
    if (mode === 'belivpedia' && !this._belivpediaLoaded) {
      this._loadLibraries();
      return;
    }
    if (mode === 'headquarters' && !this._headquartersLoaded) {
      this._loadHeadquarters();
      return;
    }

    this._rebuild();
  }

  private _modeForUrl(url: string): EdgePanelMode {
    if (this._matchesRoute(url, this._dashboardsRoute)) return 'dashboards';
    if (this._matchesRoute(url, this._headquartersRoute)) return 'headquarters';
    if (this._matchesRoute(url, this._belivpediaRoute)) return 'belivpedia';
    return 'menu';
  }

  /** La url es la sección o algo colgando de ella */
  private _matchesRoute(url: string, route: string): boolean {
    const path = (url || '').split('?')[0];
    return path === route || path.startsWith(`${route}/`);
  }

  // ──────────────────────────────────────────────────────────────
  // Árbol del menú
  // ──────────────────────────────────────────────────────────────
  private _buildMenuNodes(): void {
    const items = (this._coreMenuService.getMenu('main') || []) as CoreMenuItem[];
    this._menuNodes = this._toEdgeNodes(items);
    if (this.mode === 'menu') this._rebuild();
  }

  /** Pasa el árbol del menú al formato del panel, salvando los ocultos */
  private _toEdgeNodes(items: CoreMenuItem[]): EdgeNode[] {
    return (items || [])
      .filter(i => i && !i.hidden)
      .map(i => ({
        key: `menu-${i.id}`,
        label: this._menuLabel(i),
        // Un menú puede traer el icono elegido del catálogo ('lucide:house');
        // el resto sigue siendo clase del iconfont o imagen, como antes.
        icon: isLucideIcon(i.icon) ? lucideIconName(i.icon) : i.icon,
        lucide: isLucideIcon(i.icon),
        url: i.url,
        external: !!i.externalUrl,
        newTab: !!i.openInNewTab,
        action: i.actionMethod ? i : undefined,
        children: this._toEdgeNodes(i.children)
      }));
  }

  /**
   * Mismo texto que muestra el menú de navegación, para que no difieran. Los
   * títulos vienen de la base, no del diccionario: `title` es el español y
   * `translate` (titleEnglish) el inglés — ver `getNavLabel` en home-header.
   */
  private _menuLabel(item: CoreMenuItem): string {
    if (this._lang === 'en') return item.translate || item.title || '';
    return item.title || '';
  }

  // ──────────────────────────────────────────────────────────────
  // Árbol de dashboards
  // ──────────────────────────────────────────────────────────────
  private _reports: Report[] = [];

  private _loadDashboards(): void {
    this.loading = true;
    this._reportService.getByType(this._dashboardType)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (list) => {
          // Fuera los comunitarios: este panel es el acceso rápido a los
          // dashboards asignados al usuario
          this._reports = (list || []).filter(r => !this._isCommunity(r));
          this._dashboardsLoaded = true;
          this._buildDashboardNodes();
          this._rebuild();
          this.loading = false;
          this._cdr.detectChanges();
        },
        error: () => {
          // Queda marcado como cargado igual: sin esto se reintentaría en cada
          // navegación dentro de la sección
          this._dashboardsLoaded = true;
          this.loading = false;
          this._cdr.detectChanges();
        }
      });
  }

  // ──────────────────────────────────────────────────────────────
  // Árbol de headquarters
  // ──────────────────────────────────────────────────────────────
  private _loadHeadquarters(): void {
    this.loading = true;
    this._headquartersService.getAll()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (list) => {
          this._headquartersLoaded = true;
          this._headquartersNodes = this._toHeadquarterNodes(list || []);
          this._rebuild();
          this.loading = false;
          this._cdr.detectChanges();
        },
        error: () => {
          // Queda marcado como cargado igual: sin esto se reintentaría en cada
          // navegación dentro de la sección
          this._headquartersLoaded = true;
          this.loading = false;
          this._cdr.detectChanges();
        }
      });
  }

  /**
   * Pasa los headquarters al formato del panel. Los sitios son de SharePoint,
   * así que todos los nodos abren afuera en una pestaña nueva.
   */
  private _toHeadquarterNodes(headquarters: Headquarter[]): EdgeNode[] {
    return headquarters.map(hq => ({
      key: `hq-${hq.id}`,
      label: hq.name,
      icon: hq.icon,
      lucide: true,
      url: hq.url,
      external: !!hq.url,
      newTab: true,
      children: this._toHeadquarterChildNodes(hq.children, `${hq.id}`)
    }));
  }

  /** Las áreas anidan a cualquier profundidad (Finanzas llega a tres niveles) */
  private _toHeadquarterChildNodes(children: HeadquarterChild[], parentKey: string): EdgeNode[] {
    return (children || []).map(child => ({
      key: `hq-${parentKey}-${child.id}`,
      label: child.name,
      icon: child.icon,
      lucide: true,
      url: child.url,
      external: !!child.url,
      newTab: true,
      children: this._toHeadquarterChildNodes(child.children, `${parentKey}-${child.id}`)
    }));
  }

  /** Agrupa los dashboards por categoría, igual que el listado */
  private _buildDashboardNodes(): void {
    // Se agrupa por el nombre en español (el canónico) y se muestra el del
    // idioma activo
    const map = new Map<string, EdgeNode>();
    for (const r of this._reports) {
      const key = r.reportCategory?.name || this._t('NO_CATEGORY');
      if (!map.has(key)) {
        map.set(key, {
          key: `cat-${key}`,
          label: this._categoryLabel(r.reportCategory) || key,
          children: []
        });
      }
      map.get(key).children.push({
        key: `dash-${r.id}`,
        label: this._reportTitle(r),
        url: `${this._dashboardsRoute}/view/${r.id}`,
        state: {
          name: r.title,
          embedUrl: this._buildEmbedUrl(r),
          reportId: r.serverReferenceId,
          image: r.image,
          report: r
        }
      });
    }

    this._dashboardNodes = Array.from(map.values())
      .map(g => ({ ...g, children: g.children.sort((a, b) => a.label.localeCompare(b.label)) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  // ──────────────────────────────────────────────────────────────
  // Árbol de belivpedia: bibliotecas → carpetas → archivos
  //
  // El contenido de cada nodo se pide recién al abrirlo: cargar todas las
  // bibliotecas enteras de entrada serían decenas de llamadas.
  // ──────────────────────────────────────────────────────────────
  private _loadLibraries(): void {
    this.loading = true;
    this._repositoryBaseService.getRepositories(0, this._pageSize)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (page) => {
          this._belivpediaNodes = (page?.content || []).map(r => this._libraryNode(r));
          this._finishLibraries();
        },
        error: () => this._finishLibraries()
      });
  }

  /** Cierra la carga de bibliotecas, haya salido bien o mal */
  private _finishLibraries(): void {
    // Queda marcado como cargado igual: sin esto se reintentaría en cada
    // navegación dentro de la sección
    this._belivpediaLoaded = true;
    this.loading = false;
    this._rebuild();
    this._cdr.detectChanges();
  }

  private _libraryNode(r: Repository): EdgeNode {
    return {
      key: `lib-${r.code}`,
      label: r.title || r.code,
      icon: 'book',
      url: `${this._belivpediaRoute}/${r.code}`,
      // Las bibliotecas arrancan cerradas: abrir una es lo que pide su contenido
      defaultCollapsed: true,
      lazy: true,
      repo: r.code,
      ancestors: [],
      children: []
    };
  }

  /**
   * Pide el contenido de una biblioteca o carpeta. Se llama una sola vez por
   * nodo, la primera que se abre.
   * @param n Nodo que se está abriendo
   */
  private _loadNodeChildren(n: EdgeNode): void {
    if (n.loading || n.loaded) return;

    n.loading = true;
    const content: Content = {
      repository: n.repo,
      query: '',
      tags: false,
      ancestors: n.ancestors || []
    };

    this._repositoryFolderService.getContent(content, 0, this._pageSize, 'desc')
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (page) => {
          n.children = (page?.content || []).map(f => this._contentNode(f, n));
          this._finishNode(n);
        },
        error: () => {
          n.children = [];
          this._finishNode(n);
        }
      });
  }

  /** Cierra la carga de un nodo, haya salido bien o mal */
  private _finishNode(n: EdgeNode): void {
    // Las carpetas primero y, dentro de cada grupo, por nombre
    n.children.sort((a, b) => {
      if (!!a.lazy !== !!b.lazy) return a.lazy ? -1 : 1;
      return a.label.localeCompare(b.label);
    });

    n.loading = false;
    n.loaded = true;
    n.empty = n.children.length === 0;
    this._rebuild();
    this._cdr.detectChanges();
  }

  /** Pasa un archivo o carpeta al formato del panel */
  private _contentNode(f: FileContent, parent: EdgeNode): EdgeNode {
    const isFolder = f.contentType === this._folderType;

    if (isFolder) {
      return {
        key: `dir-${f.id}`,
        label: f.title || '',
        icon: 'folder',
        // La carpeta se abre en la pantalla de belivpedia, con la ruta que
        // arma la navegación normal: <padre>/<id>|<título>
        url: `${parent.url}/${f.id}|${f.title}`,
        defaultCollapsed: true,
        lazy: true,
        repo: parent.repo,
        ancestors: [...(parent.ancestors || []), f.id],
        children: []
      };
    }

    // Los contenidos de tipo URL apuntan a un sitio externo; el resto se abre
    // en el visor de belivpedia
    const isLink = f.contentType === 'URL';
    return {
      key: `file-${f.id}`,
      label: f.title || f.fileName || '',
      icon: this._contentIcon(f.contentType),
      url: isLink ? this._externalUrl(f.url) : `${this._belivpediaRoute}/view/${f.id}`,
      external: isLink,
      newTab: isLink
    };
  }

  /** Ícono (feather) según el tipo de contenido */
  private _contentIcon(contentType: string): string {
    switch ((contentType || '').toUpperCase()) {
      case 'URL': return 'link';
      case 'PDF':
      case 'WORD':
      case 'EXCEL':
      case 'POWERPOINT': return 'file-text';
      case 'IMAGE': return 'image';
      case 'VIDEO': return 'video';
      default: return 'file';
    }
  }

  /** Los links guardados sin esquema se abren igual */
  private _externalUrl(url: string): string {
    const value = (url || '').trim();
    if (!value) return '';
    return value.includes('http') ? value : `https://${value}`;
  }

  // ──────────────────────────────────────────────────────────────
  // Búsqueda
  // ──────────────────────────────────────────────────────────────
  onSearch(): void {
    this._rebuild();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this._rebuild();
  }

  /** Deja el árbol del modo activo, filtrado por la búsqueda */
  private _rebuild(): void {
    this.nodes = this._filter(this._sourceNodes(), (this.searchTerm || '').toLowerCase().trim());
  }

  /** Árbol completo (sin filtrar) del modo activo */
  private _sourceNodes(): EdgeNode[] {
    if (this.mode === 'dashboards') return this._dashboardNodes;
    if (this.mode === 'belivpedia') return this._belivpediaNodes;
    if (this.mode === 'headquarters') return this._headquartersNodes;
    return this._menuNodes;
  }

  /**
   * Filtra el árbol: se conserva la rama si el nodo empata o si le queda algún
   * hijo, así el resultado no pierde su categoría.
   */
  private _filter(nodes: EdgeNode[], term: string): EdgeNode[] {
    if (!term) return nodes;

    const result: EdgeNode[] = [];
    for (const n of nodes) {
      const children = this._filter(n.children || [], term);
      const matches = n.label.toLowerCase().includes(term);

      // Si empata el padre se muestra completo; si no, solo lo que empató
      if (matches) result.push(n);
      else if (children.length > 0) result.push({ ...n, children });
    }
    return result;

    // Ojo: en belivpedia la búsqueda solo alcanza lo que ya se abrió. Para
    // buscar en toda la biblioteca está el buscador de la pantalla.
  }

  // ──────────────────────────────────────────────────────────────
  // Árbol
  // ──────────────────────────────────────────────────────────────
  toggleNode(n: EdgeNode): void {
    const collapsed = this.isCollapsed(n);

    if (this._toggled.has(n.key)) this._toggled.delete(n.key);
    else this._toggled.add(n.key);

    // Al abrirlo por primera vez se pide su contenido
    if (collapsed && n.lazy) this._loadNodeChildren(n);
  }

  isCollapsed(n: EdgeNode): boolean {
    // Buscando se muestran todos los resultados, sin importar lo plegado
    if (this.searchTerm) return false;

    // La mayoría arranca abierto y las bibliotecas plegadas; el set guarda a
    // quiénes el usuario les invirtió ese estado
    const flipped = this._toggled.has(n.key);
    return flipped ? !n.defaultCollapsed : !!n.defaultCollapsed;
  }

  /** El nodo puede desplegarse: tiene hijos o todavía no los pidió */
  hasBranch(n: EdgeNode): boolean {
    return !!n.children?.length || (!!n.lazy && !n.loaded);
  }

  /** El nodo apunta a la pantalla que se está viendo */
  isActive(n: EdgeNode): boolean {
    if (!n.url || n.external || n.action) return false;
    return this._normalize(this._currentUrl) === this._normalize(n.url);
  }

  private _normalize(url: string): string {
    return (url || '').split('?')[0].replace(/^\/+|\/+$/g, '');
  }

  /** Abre el nodo: navega a su ruta (o al sitio externo) y cierra el panel */
  openNode(n: EdgeNode): void {
    if (!n?.url) return;

    this.close();

    if (n.action) {
      this._menuActionService.run(n.action);
      return;
    }

    if (n.external) {
      window.open(n.url, n.newTab ? '_blank' : '_self');
      return;
    }

    if (n.newTab) {
      window.open(this._router.serializeUrl(this._router.createUrlTree([n.url])), '_blank');
      return;
    }

    this._router.navigate([n.url], n.state ? { state: n.state } : {});
  }

  // ──────────────────────────────────────────────────────────────
  // Helpers
  // ──────────────────────────────────────────────────────────────

  /** Nombre del dashboard en el idioma activo (titleEnglish vive en la base) */
  private _reportTitle(r: Report): string {
    if (this._lang === 'en') return (r?.titleEnglish || '').trim() || r?.title || '';
    return r?.title || '';
  }

  private _categoryLabel(c: Report['reportCategory']): string {
    if (this._lang === 'en') return (c?.nameEnglish || '').trim() || c?.name || '';
    return c?.name || '';
  }

  private _isCommunity(r: Report): boolean {
    return (r?.reportCategory?.name || '').trim().toLowerCase() === this._communityCategory.toLowerCase();
  }

  private _t(key: string): string {
    return this._translateService.instant(`COMMON.DASHBOARD_TEXT.LIST.${key}`);
  }

  /**
   * URL embed del reporte, con el mismo formato que arma el listado, para que
   * el visor reciba el state completo y no tenga que ir al backend.
   */
  private _buildEmbedUrl(r: Report): string {
    const base = r.serverUrl || 'https://app.powerbi.com/reportEmbed';
    if (!r.serverReferenceId) return base;

    const url = new URL(base);
    url.searchParams.set('reportId', r.serverReferenceId);
    if (r.groupId) url.searchParams.set('groupId', r.groupId);
    if (r.ctid) url.searchParams.set('ctid', r.ctid);
    return url.toString();
  }

  trackByKey(_: number, n: EdgeNode): string { return n.key; }
}
